<template>
  <div class="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-emerald-950 flex items-center justify-center p-4">
    <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full overflow-hidden animate-zoomIn">

      <!-- Header -->
      <div class="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-8 text-center">
        <div class="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
          <Icon icon="mdi:check-decagram" class="text-white text-5xl" />
        </div>
        <h1 class="text-2xl font-black text-white mb-1">¡Registro Exitoso!</h1>
        <p class="text-sm text-emerald-100 font-medium">
          {{ esPedidoExistente ? 'Tu pedido ha sido registrado' : 'Tu cuenta ha sido creada correctamente' }}
        </p>
      </div>

      <!-- Cuerpo -->
      <div class="p-6 space-y-5">

        <!-- 🎫 N° DE PEDIDO DESTACADO -->
        <div v-if="datos?.numeroPedido" class="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-5 text-center shadow-lg shadow-indigo-500/30">
          <div class="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
          <div class="absolute -left-6 -bottom-6 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>

          <div class="relative">
            <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full mb-2">
              <Icon icon="mdi:ticket-confirmation" class="text-white text-xs" />
              <span class="text-[9px] font-black text-white uppercase tracking-widest">Tu N° de Pedido</span>
            </div>

            <p class="text-3xl sm:text-4xl font-black text-white tracking-wider mb-2">
              {{ datos.numeroPedido }}
            </p>

            <p class="text-[10px] text-indigo-100 font-bold">
              <Icon icon="mdi:information-outline" class="inline text-xs" />
              Guarda este número para consultar tu pedido
            </p>
          </div>
        </div>

        <!-- ⭐ CÓDIGO DE ENTREGA DESTACADO -->
        <div v-if="datos?.codigoEntrega" class="relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 dark:from-black dark:to-gray-900 rounded-2xl p-5 text-center shadow-2xl shadow-emerald-500/20 border-2 border-emerald-500/40">
          <div class="absolute -right-8 -top-8 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div class="absolute -left-8 -bottom-8 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>

          <div class="relative">
            <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/20 backdrop-blur-md rounded-full mb-3 border border-emerald-500/30">
              <Icon icon="mdi:lock-outline" class="text-emerald-400 text-xs" />
              <span class="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Código de Entrega</span>
            </div>

            <p class="text-4xl sm:text-5xl font-black text-emerald-400 tracking-[0.3em] mb-3 font-mono"
               style="text-shadow: 0 0 20px rgba(16, 185, 129, 0.5);">
              {{ formatearCodigo(datos.codigoEntrega) }}
            </p>

            <div class="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 rounded-lg">
              <Icon icon="mdi:alert-circle-outline" class="text-amber-400 text-xs flex-shrink-0" />
              <p class="text-[9px] text-amber-300 font-black uppercase tracking-wider">
                Dáselo al delivery solo al recibir tu gas
              </p>
            </div>
          </div>
        </div>

        <!-- ⭐⭐ BOTÓN "REPORTAR MI PAGO" — SOLO SI EL PEDIDO ESTÁ CONFIRMADO -->
        <div v-if="puedeReportarPago" class="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-5 shadow-2xl shadow-orange-500/40">
          <div class="absolute -right-6 -top-6 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
          <div class="absolute -left-6 -bottom-6 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>

          <div class="relative text-center">
            <div class="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 border border-white/30">
              <Icon icon="mdi:cash-check" class="text-white text-3xl" />
            </div>

            <p class="text-[10px] font-black text-amber-100 uppercase tracking-widest mb-1">
              Acción requerida
            </p>
            <h2 class="text-lg font-black text-white mb-2">
              Reporta tu pago para activar el pedido
            </h2>
            <p class="text-[11px] text-amber-100 font-semibold leading-relaxed mb-4">
              Tu pedido está <strong>CONFIRMADO</strong>.<br>
              Ahora reporta tu pago móvil y un operador lo verificará.
            </p>

            <button
              @click="irAReportarPago"
              class="w-full py-4 bg-white text-orange-600 rounded-xl font-black text-sm uppercase tracking-widest hover:bg-amber-50 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <Icon icon="mdi:file-document-edit-outline" class="text-xl" />
              📄 Reportar mi pago
            </button>
          </div>
        </div>

        <!-- Próximo paso -->
        <div class="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-4 border border-blue-200 dark:border-blue-800">
          <div class="flex items-start gap-3">
            <Icon icon="mdi:information" class="text-blue-600 text-xl mt-0.5 flex-shrink-0" />
            <div>
              <p class="text-xs font-black text-blue-700 dark:text-blue-300 uppercase tracking-widest mb-1">
                Próximo paso
              </p>
              <p class="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                {{ textoProximoPaso }}
              </p>
            </div>
          </div>
        </div>

        <!-- Datos registrados -->
        <div v-if="datos" class="space-y-3">
          <h3 class="text-xs font-black text-gray-400 uppercase tracking-widest">
            Datos registrados
          </h3>

          <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 space-y-3 border border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-start gap-4">
              <span class="text-[10px] font-black text-gray-400 uppercase">Nombre</span>
              <span class="text-xs font-bold text-gray-900 dark:text-white text-right">
                {{ datos.nombre }} {{ datos.apellido }}
              </span>
            </div>
            <div class="flex justify-between items-start gap-4">
              <span class="text-[10px] font-black text-gray-400 uppercase">Cédula</span>
              <span class="text-xs font-bold text-gray-900 dark:text-white">{{ datos.cedula }}</span>
            </div>
            <div class="flex justify-between items-start gap-4">
              <span class="text-[10px] font-black text-gray-400 uppercase">Teléfono</span>
              <span class="text-xs font-bold text-gray-900 dark:text-white">{{ datos.telefono }}</span>
            </div>
            <div class="flex justify-between items-start gap-4">
              <span class="text-[10px] font-black text-gray-400 uppercase">Dirección</span>
              <span class="text-xs font-bold text-gray-900 dark:text-white text-right">{{ datos.direccion }}</span>
            </div>
          </div>
        </div>

        <!-- Servicios (si vienen) -->
        <div v-if="datos?.servicios && datos.servicios.length > 0" class="space-y-3">
          <h3 class="text-xs font-black text-gray-400 uppercase tracking-widest">
            Servicios solicitados
          </h3>

          <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
            <div
              v-for="(s, i) in datos.servicios"
              :key="i"
              class="flex justify-between items-center py-2 first:pt-0 last:pb-0"
            >
              <div>
                <p class="text-xs font-bold text-gray-900 dark:text-white">{{ s.nombre }}</p>
                <p class="text-[10px] text-gray-500 dark:text-gray-400">Cant: {{ s.cantidad }}</p>
              </div>
              <p class="text-xs font-black text-emerald-600 dark:text-emerald-400">
                Bs. {{ formatearNumero(s.subtotalBs) }}
              </p>
            </div>

            <div class="flex justify-between items-center pt-3 mt-1">
              <span class="text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Total</span>
              <span class="text-lg font-black text-emerald-600 dark:text-emerald-400">
                Bs. {{ formatearNumero(datos.totalBs) }}
              </span>
            </div>
          </div>
        </div>

        <!-- 🎫 BOTONES DE ACCIÓN -->
        <div class="space-y-2 pt-2">
          <!-- Descargar Orden PDF -->
          <button
            v-if="datos?.numeroPedido"
            @click="descargarOrdenPDF"
            :disabled="descargando"
            class="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon
              :icon="descargando ? 'mdi:loading' : 'mdi:file-pdf-box'"
              :class="{ 'animate-spin': descargando }"
              class="text-lg"
            />
            {{ descargando ? 'Generando PDF...' : 'Descargar Orden en PDF' }}
          </button>

          <!-- Imprimir Ticket -->
          <button
            v-if="datos?.numeroPedido"
            @click="imprimirTicket"
            class="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2"
          >
            <Icon icon="mdi:printer" class="text-base" />
            Imprimir Ticket
          </button>

          <!-- Volver al Inicio -->
          <button
            @click="volverAlInicio"
            class="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg flex items-center justify-center gap-2"
          >
            <Icon icon="mdi:home" class="text-base" />
            Volver al Inicio
          </button>
        </div>

        <!-- Ayuda -->
        <div class="text-center pt-2">
          <p class="text-[10px] text-gray-400 dark:text-gray-500 font-medium leading-relaxed">
            ¿Problemas con tu pedido?<br>
            Contáctanos por WhatsApp: <strong class="text-gray-600 dark:text-gray-300">{{ empresa.telefono || '0424-5976200' }}</strong>
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerDatosEmpresa } from '@/utils/tenantConfig';
import { imprimirOrdenPedidoPDF } from '@/utils/ordenPedidoPDFGenerator';
import { imprimirTicketPedido } from '@/utils/ticketGasGenerator';

export default {
  name: 'ConfirmacionRegistroGas',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      datos: null,
      descargando: false,
      empresa: {
        nombre: 'EXTRA GAS',
        rif: 'J-41023119-0',
        telefono: '0424-5976200'
      }
    };
  },
  computed: {
    esPedidoExistente() {
      return this.datos?.modo === 'existente';
    },

    // ⭐ ¿Puede reportar pago? Solo si el pedido está CONFIRMADO y tiene idPedidoRecarga
    puedeReportarPago() {
      const estadosValidos = ['CONFIRMADO', 'Confirmado', 'PAGO_PENDIENTE'];
      const tieneEstado = estadosValidos.includes(this.datos?.estadoPedido);
      const tieneId = !!(this.datos?.idPedidoRecarga);
      return tieneEstado && tieneId;
    },

    // ⭐ Texto del próximo paso, según el estado
    textoProximoPaso() {
      if (this.puedeReportarPago) {
        return 'Tu pedido está confirmado. Ahora reporta tu pago móvil con el botón de arriba. Un operador lo verificará en las próximas horas para coordinar la entrega.';
      }
      if (this.esPedidoExistente) {
        return 'Un operador revisará tu pedido y te contactará al teléfono para verificar el pago y coordinar la entrega.';
      }
      return 'Un operador revisará tu registro y te contactará al teléfono para verificar tu cuenta antes de activar los pedidos.';
    }
  },
  mounted() {
    this.cargarEmpresa();
    // Recuperar los datos enviados desde el registro
    try {
      const raw = sessionStorage.getItem('gas_registro_confirmacion');
      if (raw) {
        this.datos = JSON.parse(raw);
        console.log('📋 Datos de confirmación cargados:', this.datos);
      }
    } catch (e) {
      console.warn('No se pudieron leer los datos de confirmación');
    }
  },
  methods: {
    cargarEmpresa() {
      try {
        const datos = obtenerDatosEmpresa();
        if (datos) {
          this.empresa = {
            nombre: datos.nombreComercial || datos.nombre || this.empresa.nombre,
            rif: datos.rif || this.empresa.rif,
            telefono: datos.telefono1 || datos.telefono2 || this.empresa.telefono
          };
        }
      } catch (e) {
        console.warn('No se pudo cargar la config de la empresa');
      }
    },

    formatearNumero(valor, decimales = 2) {
      if (valor === null || valor === undefined || valor === '') return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales
      }).format(num);
    },

    // ⭐ Formatear código de entrega a 6 dígitos
    formatearCodigo(codigo) {
      if (!codigo) return '------';
      return String(codigo).padStart(6, '0');
    },

    // ⭐ Ir a la pantalla de reportar pago
    irAReportarPago() {
      if (!this.datos?.idPedidoRecarga) {
        this.toast.error('No se encontró el ID del pedido');
        return;
      }
      this.$router.push({
        path: '/gas/reportar-pago',
        query: { pedido: this.datos.idPedidoRecarga }
      });
    },

    async descargarOrdenPDF() {
      if (!this.datos) {
        this.toast.error('No hay datos del pedido para generar el PDF');
        return;
      }

      this.descargando = true;
      try {
        await imprimirOrdenPedidoPDF(this.datos, this.empresa);
        this.toast.success('Orden generada correctamente');
      } catch (error) {
        console.error('💥 Error generando PDF:', error);
        this.toast.error(error?.message || 'Error al generar el PDF');
      } finally {
        this.descargando = false;
      }
    },

    imprimirTicket() {
      if (!this.datos) {
        this.toast.error('No hay datos del pedido para imprimir');
        return;
      }

      try {
        const pedidoParaTicket = {
          numeroPedido: this.datos.numeroPedido,
          fechaPedido: this.datos.fechaPedido || new Date().toISOString(),
          nombreCliente: `${this.datos.nombre} ${this.datos.apellido}`,
          cedulaCliente: this.datos.cedula,
          telefonoCliente: this.datos.telefono,
          direccionEntrega: this.datos.direccion,
          puntoReferencia: this.datos.puntoReferencia,
          urlMapaEntrega: this.datos.urlMapa,
          nombreZona: this.datos.nombreZona || 'Sin zona',
          codigoEntrega: this.datos.codigoEntrega,
          totalBs: this.datos.totalBs,
          estado: this.datos.estadoPedido || 'PENDIENTE',
          estadoPago: 'pendiente',
          detalles: (this.datos.servicios || []).map(s => ({
            nombreTipoRecarga: s.nombre,
            cantidad: s.cantidad,
            precioUnitarioBs: s.precioBs,
            totalBs: s.subtotalBs
          }))
        };

        console.log('🖨️ Imprimiendo ticket:', pedidoParaTicket);
        imprimirTicketPedido(pedidoParaTicket);
        this.toast.success('Ticket generado');
      } catch (error) {
        console.error('💥 Error imprimiendo ticket:', error);
        this.toast.error(error?.message || 'Error al generar ticket');
      }
    },

    volverAlInicio() {
      sessionStorage.removeItem('gas_registro_confirmacion');
      this.$router.push('/Extragas-web');
    }
  }
};
</script>

<style scoped>
@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.25s ease-out; }

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin { animation: spin 1s linear infinite; }
</style>